import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import {
  Box, Typography, Paper, Chip, IconButton, Stack,
  useMediaQuery, useTheme, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { ChevronLeft, ChevronRight, ExpandMore } from '@mui/icons-material';
import { db } from './firebase';
import { STAGES } from './constants';
import { nowISO } from './helpers';
import { useTags } from './useTags';

export default function Pipeline() {
  const [contatti, setContatti] = useState([]);
  const navigate = useNavigate();
  const { getColor } = useTags();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => onSnapshot(collection(db, 'crm_contatti'), s =>
    setContatti(s.docs.map(d => ({ id: d.id, ...d.data() })))
  ), []);

  const move = async (id, newStage) => {
    await updateDoc(doc(db, 'crm_contatti', id), { stage: newStage, updatedAt: nowISO() });
  };

  // Card contatto riutilizzabile
  const ContactCard = ({ c, idx }) => (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Box onClick={() => navigate('/contatti?id=' + c.id)} sx={{ cursor: 'pointer' }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.nome}</Typography>
        {c.azienda && <Typography variant="caption" color="text.secondary">{c.azienda}</Typography>}
        {c.tag && (
          <Box sx={{ mt: 0.5 }}>
            <Chip label={c.tag} size="small" sx={{ fontSize: 10, height: 18, bgcolor: getColor(c.tag), color: 'white' }} />
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
        {idx > 0 && (
          <IconButton size="small" onClick={() => move(c.id, STAGES[idx - 1].id)} sx={{ flex: 1, p: 0.25 }}>
            <ChevronLeft fontSize="small" />
          </IconButton>
        )}
        {idx < STAGES.length - 1 && (
          <IconButton size="small" onClick={() => move(c.id, STAGES[idx + 1].id)} sx={{ flex: 1, p: 0.25 }}>
            <ChevronRight fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Pipeline</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Sposta i contatti tra stage.</Typography>

      {isMobile ? (
        // ── MOBILE: accordion per stage ──
        <Stack spacing={1}>
          {STAGES.map((stage, idx) => {
            const items = contatti.filter(c => c.stage === stage.id);
            return (
              <Accordion key={stage.id} defaultExpanded={items.length > 0} disableGutters
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}
                  sx={{ bgcolor: stage.color + '15', minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: stage.color }}>{stage.label}</Typography>
                    <Chip size="small" label={items.length} sx={{ height: 18, fontSize: 10 }} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                  {items.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">Nessun contatto.</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {items.map(c => <ContactCard key={c.id} c={c} idx={idx} />)}
                    </Stack>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      ) : (
        // ── DESKTOP: kanban identico a prima ──
        <Box sx={{ display: 'grid', gridTemplateColumns: { sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 1.5 }}>
          {STAGES.map((stage, idx) => {
            const items = contatti.filter(c => c.stage === stage.id);
            return (
              <Paper key={stage.id} variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 300 }}>
                <Box sx={{ px: 1.5, py: 1, bgcolor: stage.color + '20', borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: stage.color }}>{stage.label}</Typography>
                  <Chip size="small" label={items.length} />
                </Box>
                <Stack spacing={1} sx={{ p: 1, flex: 1 }}>
                  {items.map(c => <ContactCard key={c.id} c={c} idx={idx} />)}
                </Stack>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}